import { useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Grid, Space, Input, Select, theme, type TableProps } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { DataTable } from '../../../components/DataTable';
import { EmptyState } from '../../../components/EmptyState';
import { EstadoClienteTag } from './EstadoClienteTag';
import { TipoClienteTag } from './TipoClienteTag';
import { ClienteCreateModal } from './ClienteCreateModal';
import { useClientesList } from '../api/useClientes';
import { useCatalogos } from '../../catalogos/api/useCatalogos';
import { useHasPermission } from '../../../auth/useHasPermission';
import { useT } from '../../../locales';
import { useClientesListStore } from '../../../stores/clientes-list-store';
import { useDebouncedValue, DEBOUNCE_MS } from '../../../lib/useDebouncedValue';
import { paginationProps, makePaginacionHandler } from '../../../lib/paginacion';
import { Permission, type ClienteResponse } from '@aid/shared';
import { parseServerSorter, serverSortProps, type ServerSortState } from '../../../lib/server-sort';

const INTERACTIVE_ROW_TARGET_SELECTOR = [
  'a',
  'button',
  'input',
  'select',
  'textarea',
  'summary',
  '[contenteditable="true"]',
  '[role="button"]',
  '[role="checkbox"]',
  '[role="link"]',
  '[role="switch"]',
  '[data-row-interactive]',
].join(', ');

function isInteractiveRowTarget(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(INTERACTIVE_ROW_TARGET_SELECTOR) !== null;
}

export function ClientesTable() {
  const t = useT();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const search = useClientesListStore((s) => s.search);
  const estado = useClientesListStore((s) => s.estado);
  const tipo = useClientesListStore((s) => s.tipo);
  const page = useClientesListStore((s) => s.page);
  const pageSize = useClientesListStore((s) => s.pageSize);
  const setSearch = useClientesListStore((s) => s.setSearch);
  const setEstado = useClientesListStore((s) => s.setEstado);
  const setTipo = useClientesListStore((s) => s.setTipo);
  const setPage = useClientesListStore((s) => s.setPage);
  const setPageSize = useClientesListStore((s) => s.setPageSize);
  const [modalOpen, setModalOpen] = useState(false);
  const [sort, setSort] = useState<ServerSortState>({});

  // El input escribe en el store al instante; la query espera 350ms sin
  // tipeo para no disparar un request por tecla.
  const searchDebounced = useDebouncedValue(search, DEBOUNCE_MS);

  const { data, isLoading, error } = useClientesList({
    limit: pageSize,
    offset: (page - 1) * pageSize,
    search: searchDebounced || undefined,
    estado,
    tipo,
    ...sort,
  });

  const { data: estadosCatalogo } = useCatalogos('ESTADO_CLIENTE');
  const { data: tiposCatalogo } = useCatalogos('TIPO_CLIENTE');
  const canCreate = useHasPermission(Permission.CLIENTES_CREATE);

  if (error) {
    return <EmptyState title={t('pages.clientes.errorLoad')} />;
  }

  const columns = [
    {
      title: t('pages.clientes.codigo'),
      dataIndex: 'ip' as keyof ClienteResponse,
      key: 'ip',
      width: 110,
      ...serverSortProps('ip', sort),

      render: (val: ClienteResponse['ip']) => val ?? '-',
    },
    {
      title: t('pages.clientes.name'),
      dataIndex: 'nombre' as keyof ClienteResponse,
      key: 'nombre',
      ...serverSortProps('nombre', sort),
      render: (val: string | null) => val ?? '-',
    },
    {
      title: t('pages.clientes.identificacion'),
      dataIndex: 'identificacion' as keyof ClienteResponse,
      key: 'identificacion',
      ...serverSortProps('identificacion', sort),
      render: (val: string | null) => val ?? '-',
    },
    {
      title: t('pages.clientes.phone'),
      dataIndex: 'telefonoPrincipal' as keyof ClienteResponse,
      key: 'telefonoPrincipal',
      render: (val: string | null) => val ?? '-',
    },
    {
      title: t('pages.clientes.email'),
      dataIndex: 'email' as keyof ClienteResponse,
      key: 'email',
      width: 90,
      ...serverSortProps('email', sort),

      render: (val: string | null) =>
        val ? val : <span style={{ color: token.colorTextTertiary }}>—</span>,
    },
    {
      title: t('pages.clientes.status'),
      dataIndex: 'estadoCliente' as keyof ClienteResponse,
      key: 'estadoCliente',
      render: (val: ClienteResponse['estadoCliente']) => <EstadoClienteTag estado={val} />,
    },
    {
      title: t('pages.clientes.tipo'),
      dataIndex: 'tipoCliente' as keyof ClienteResponse,
      key: 'tipoCliente',
      render: (val: ClienteResponse['tipoCliente']) => <TipoClienteTag tipo={val} />,
    },
  ];

  // setPageSize del store resetea la página a 1; acá solo se enruta el cambio.
  const handlePaginationChange = makePaginacionHandler(pageSize, setPageSize, setPage);
  const handleTableChange: NonNullable<TableProps<ClienteResponse>['onChange']> = (
    pagination,
    _filters,
    sorter,
  ) => {
    const nextSort = parseServerSorter(sorter);
    if (nextSort.sortBy !== sort.sortBy || nextSort.sortOrder !== sort.sortOrder) {
      setSort(nextSort);
      setPage(1);
      return;
    }
    handlePaginationChange(pagination);
  };
  const basePagination = paginationProps(t, { page, pageSize, total: data?.total ?? 0 });
  const pagination = {
    ...basePagination,
    simple: isMobile,
    showSizeChanger: !isMobile,
    showTotal: isMobile ? undefined : basePagination.showTotal,
  };
  const tableThemeVars = {
    '--clientes-table-row-hover-bg': token.colorFillTertiary,
    '--clientes-table-row-focus-outline': token.colorPrimary,
  } as CSSProperties;
  const openClientDetail = (record: ClienteResponse) => navigate(`/clientes/${record.id}`);

  return (
    <div className="clientes-table" style={tableThemeVars}>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <Space wrap size={[8, 8]} className="table-filters">
          <Input
            id="clientes-search"
            name="search"
            aria-label={t('pages.clientes.searchPlaceholder')}
            placeholder={t('pages.clientes.searchPlaceholder')}
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            style={{ width: 250 }}
            allowClear
          />
          <Select
            placeholder={t('pages.clientes.filterEstado')}
            allowClear
            style={{ width: 180 }}
            value={estado}
            onChange={(val) => {
              setEstado(val);
            }}
            options={(estadosCatalogo ?? []).map((e) => ({
              label: e.value ?? e.key ?? `#${e.id}`,
              value: e.id,
            }))}
          />
          <Select
            placeholder={t('pages.clientes.filterTipo')}
            allowClear
            style={{ width: 180 }}
            value={tipo}
            onChange={(val) => {
              setTipo(val);
            }}
            options={(tiposCatalogo ?? []).map((tipoCatalogo) => ({
              label: tipoCatalogo.value ?? tipoCatalogo.key ?? `#${tipoCatalogo.id}`,
              value: tipoCatalogo.id,
            }))}
          />
        </Space>
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
            {t('pages.clientes.create')}
          </Button>
        )}
      </div>
      <DataTable<ClienteResponse>
        columns={columns}
        dataSource={data?.data}
        rowKey="id"
        loading={isLoading}
        pagination={pagination}
        onChange={handleTableChange}
        rowClassName={() => 'clientes-table__row'}
        onRow={(record) => ({
          tabIndex: 0,
          onClick: (event) => {
            if (!isInteractiveRowTarget(event.target)) {
              openClientDetail(record);
            }
          },
          onKeyDown: (event) => {
            if (
              isInteractiveRowTarget(event.target) ||
              (event.key !== 'Enter' &&
                event.key !== ' ' &&
                event.key !== 'Spacebar' &&
                event.key !== 'Space')
            ) {
              return;
            }
            event.preventDefault();
            openClientDetail(record);
          },
        })}
      />
      <ClienteCreateModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
}
